import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { MapPin, Plus, Check, ChevronDown, ArrowRight } from "lucide-react";

const HINT_KEY = "jirani_community_hint_seen";

export default function MtaaSwitcher() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/neighborhoods");
      setState(data);
    } catch { /* silent */ }
  };

  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => {
    if (!user || !state) return;
    if (typeof window === "undefined") return;
    const seen = localStorage.getItem(HINT_KEY);
    const joined = (state.neighborhoods || []).length;
    if (!seen && joined > 0) {
      const t = setTimeout(() => setShowHint(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user, state]);

  const dismissHint = () => {
    setShowHint(false);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
  };

  const join = async (name) => {
    setBusy(true);
    try {
      const { data } = await api.post("/neighborhoods/join", { name });
      setState((s) => ({ ...(s || {}), ...data }));
      toast.success(`Joined ${name}`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const switchTo = async (name) => {
    setBusy(true);
    try {
      const { data } = await api.post("/neighborhoods/switch", { name });
      setState((s) => ({ ...(s || {}), ...data }));
      toast.success(`Now viewing ${name}`);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!user || !state) return null;

  const active = state.active_neighborhood || "Nairobi";
  const joined = state.neighborhoods || [];
  const canJoin = (state.popular || []).filter((h) => !joined.includes(h));

  return (
    <div className="relative">
      <DropdownMenu open={open} onOpenChange={(v) => { setOpen(v); if (v) dismissHint(); }}>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="community-switcher"
            className={`jirani-pill hidden items-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-xs font-medium text-[#2E473B] hover:bg-[#F9F7F4] md:inline-flex ${
              showHint ? "border-[#D95D39] ring-2 ring-[#D95D39]/30" : "border-black/5"
            }`}
            disabled={busy}
          >
            <MapPin className="h-3.5 w-3.5 text-[#D95D39]" />
            <span className="max-w-[110px] truncate">{active}</span>
            <ChevronDown className={`h-3.5 w-3.5 text-[#2E473B]/70 transition-transform ${open ? "rotate-180" : ""}`} />
            {showHint && (
              <span className="pointer-events-none absolute -right-1 -top-1 flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#D95D39] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#D95D39]" />
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Your neighbourhoods</DropdownMenuLabel>
          {joined.length === 0 && (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              Join a community to switch context
            </div>
          )}
          {joined.map((h) => (
            <DropdownMenuItem
              key={h}
              data-testid={`community-switch-${h.replace(/\s+/g, "-")}`}
              onClick={() => switchTo(h)}
              className="cursor-pointer"
            >
              <MapPin className="mr-2 h-3.5 w-3.5" />
              <span className="flex-1">{h}</span>
              {h === active && <Check className="h-4 w-4 text-[#D95D39]" />}
            </DropdownMenuItem>
          ))}
          {active && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="community-visit-page"
                onClick={() => navigate(`/community/${encodeURIComponent(active)}`)}
                className="cursor-pointer text-[#D95D39]"
              >
                <ArrowRight className="mr-2 h-3.5 w-3.5" /> Visit {active} page
              </DropdownMenuItem>
            </>
          )}

          {canJoin.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">
                Popular in Nairobi
              </DropdownMenuLabel>
              {canJoin.slice(0, 8).map((h) => (
                <DropdownMenuItem
                  key={h}
                  data-testid={`community-join-${h.replace(/\s+/g, "-")}`}
                  onClick={() => join(h)}
                  className="cursor-pointer text-[#2E473B]"
                >
                  <Plus className="mr-2 h-3.5 w-3.5" /> Join {h}
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {showHint && (
        <div
          data-testid="community-hint-callout"
          className="absolute right-0 top-full z-50 mt-2 hidden w-64 rounded-xl border border-black/5 bg-white p-3 shadow-lg md:block"
          role="tooltip"
        >
          <div
            className="absolute -top-1.5 right-6 h-3 w-3 rotate-45 border-l border-t border-black/5 bg-white"
            aria-hidden
          />
          <div className="text-xs font-semibold text-[#1A1A1A]" style={{ fontFamily: "Outfit" }}>
            Switch your community here
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-[#4A4A4A]">
            Tap this pill any time to jump between neighbourhoods or join a new one.
          </p>
          <button
            data-testid="community-hint-dismiss"
            onClick={dismissHint}
            className="jirani-pill mt-2 rounded-full bg-[#2E473B] px-3 py-1 text-[10px] font-semibold text-white"
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}
